import { SwitchRow } from '@components/SwitchRow/SwitchRow';
import formCss from '@lib/style/form.module.scss';
import { Autocomplete, FormControlLabel, Radio, RadioGroup, TextField } from '@mui/material';
import { useState } from 'react';
import type { ComposeFieldsProps } from '../../types';
import {
  YOUTUBE_PRIVACY_LEVELS,
  type YouTubeAccountCapabilities,
  type YouTubePostContent,
  type YouTubePrivacy,
} from './index';
import {
  utf8Length,
  YOUTUBE_DESCRIPTION_MAX_BYTES,
  YOUTUBE_TAGS_MAX_LENGTH,
  YOUTUBE_TITLE_MAX_LENGTH,
  youtubeTagsLength,
} from './limits';

/** Each level as YouTube Studio words it; `private` is the draft equivalent, so its hint says where the post waits. */
const PRIVACY_OPTIONS: Record<YouTubePrivacy, { label: string; hint: string }> = {
  public: { label: 'Public', hint: 'Anyone can find and watch it.' },
  unlisted: { label: 'Unlisted', hint: 'Anyone with the link can watch it.' },
  private: {
    label: 'Private',
    hint: 'The post stays on the channel for you to publish from YouTube Studio.',
  },
};

/** Tags typed as one comma-separated run become one tag each; blanks drop out. */
const splitTags = (text: string): string[] =>
  text
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);

/**
 * YouTube's part of the compose form, in the order a YouTube Studio upload
 * asks: title, description, tags, visibility, the made-for-kids statement,
 * then the synthetic-media disclosure and the subscriber notification.
 */
export function YouTubeComposeFields({
  content,
  onChange,
  capabilities,
  disabled,
}: ComposeFieldsProps<YouTubePostContent, YouTubeAccountCapabilities>) {
  // The tag being typed, before Enter or a comma commits it.
  const [tagDraft, setTagDraft] = useState('');
  const tags = content.tags ?? [];
  // A channel declared made for kids makes every post so: the choice narrows to
  // Yes, unless the stored row already says No, which stays visible rather than
  // flipping a legal statement on an edit the user did not make.
  const kidsForced = capabilities?.channelMadeForKids === true && content.madeForKids !== false;

  const set = (patch: Partial<YouTubePostContent>) => onChange({ ...content, ...patch });
  const setTags = (next: string[]) => set({ tags: next.length > 0 ? next : undefined });

  return (
    <div className={formCss.composeFields}>
      <div className={formCss.field}>
        <span className={formCss.fieldLabel}>Title</span>
        <TextField
          className={formCss.textInput}
          placeholder="Required"
          value={content.title}
          disabled={disabled}
          onChange={(e) => set({ title: e.target.value })}
          helperText={`${content.title.length.toLocaleString()} / ${YOUTUBE_TITLE_MAX_LENGTH.toLocaleString()}`}
          slotProps={{ htmlInput: { maxLength: YOUTUBE_TITLE_MAX_LENGTH } }}
        />
      </div>

      <div className={formCss.field}>
        <span className={formCss.fieldLabel}>Description</span>
        {/* YouTube caps the description in bytes, which `maxLength` cannot count: the gate under the submit button holds the line. */}
        <TextField
          className={formCss.textInput}
          multiline
          minRows={3}
          placeholder="Tell viewers about this post #hashtags @mentions"
          value={content.description ?? ''}
          disabled={disabled}
          onChange={(e) => set({ description: e.target.value || undefined })}
          helperText={`${utf8Length(content.description ?? '').toLocaleString()} / ${YOUTUBE_DESCRIPTION_MAX_BYTES.toLocaleString()} bytes`}
        />
      </div>

      <div className={formCss.field}>
        <span className={formCss.fieldLabel}>Tags</span>
        <Autocomplete
          className={formCss.tagInput}
          multiple
          freeSolo
          // A tag typed and left without Enter is kept on blur rather than discarded (MUI's default for free text).
          autoSelect
          options={[]}
          value={tags}
          inputValue={tagDraft}
          disabled={disabled}
          onChange={(_, next) => setTags(next.flatMap(splitTags))}
          onInputChange={(_, text, reason) => {
            // A comma commits what is typed before it, as YouTube Studio does; Enter is the Autocomplete's own.
            if (reason === 'input' && text.includes(',')) {
              const committed = splitTags(text);
              if (committed.length > 0) setTags([...tags, ...committed]);
              setTagDraft('');
              return;
            }
            setTagDraft(text);
          }}
          slotProps={{ chip: { size: 'small' } }}
          renderInput={(params) => (
            <TextField
              {...params}
              className={formCss.textInput}
              placeholder={tags.length === 0 ? 'Press Enter or type a comma after each tag' : ''}
              helperText={`${youtubeTagsLength(tags).toLocaleString()} / ${YOUTUBE_TAGS_MAX_LENGTH.toLocaleString()}`}
            />
          )}
        />
      </div>

      <div className={formCss.field}>
        <span className={formCss.fieldLabel}>Visibility</span>
        <RadioGroup
          className={formCss.radioGroup}
          value={content.privacy}
          onChange={(e) => set({ privacy: e.target.value as YouTubePrivacy })}
        >
          {YOUTUBE_PRIVACY_LEVELS.map((level) => (
            <FormControlLabel
              key={level}
              value={level}
              control={<Radio />}
              disabled={disabled}
              className={formCss.radioOption}
              label={
                <>
                  <span>{PRIVACY_OPTIONS[level].label}</span>
                  <span className={formCss.fieldHint}>{PRIVACY_OPTIONS[level].hint}</span>
                </>
              }
            />
          ))}
        </RadioGroup>
      </div>

      <div className={formCss.field}>
        <span className={formCss.fieldLabel}>Made for kids</span>
        {kidsForced && (
          <span className={formCss.fieldHint}>
            Your channel is set to made for kids, which applies to everything it posts.
          </span>
        )}
        <RadioGroup
          className={formCss.radioGroup}
          // A radio group carries strings: 'yes' and 'no' here, '' while the user has not said.
          value={content.madeForKids === undefined ? '' : content.madeForKids ? 'yes' : 'no'}
          onChange={(e) => set({ madeForKids: e.target.value === 'yes' })}
        >
          <FormControlLabel
            value="yes"
            control={<Radio />}
            disabled={disabled}
            label="Yes, it’s made for kids"
          />
          {!kidsForced && (
            <FormControlLabel
              value="no"
              control={<Radio />}
              disabled={disabled}
              label="No, it’s not made for kids"
            />
          )}
        </RadioGroup>
      </div>

      <div className={formCss.switchStack}>
        <SwitchRow
          label="Altered or synthetic content"
          hint="YouTube’s disclosure for realistic altered or synthetic media."
          checked={content.containsSyntheticMedia}
          disabled={disabled}
          onChange={(containsSyntheticMedia) => set({ containsSyntheticMedia })}
        />
        <SwitchRow
          label="Notify subscribers"
          hint="Send the channel’s subscribers a notification, as YouTube does."
          checked={content.notifySubscribers}
          disabled={disabled}
          onChange={(notifySubscribers) => set({ notifySubscribers })}
        />
      </div>
    </div>
  );
}
